import React, { useState, useEffect } from 'react';
import { AnimatePresence } from 'framer-motion';
import { Settings, Users, Phone, UserPlus, Save, X, DollarSign, Shield, Building2, Upload, Image as ImageIcon, FileText, Edit, Trash2, Plus, ChevronLeft, Anchor, Contact, Percent } from 'lucide-react';
import EditableField from './EditableField';
import BulkImporter from './BulkImporter';
import UserManagement from './UserManagement';
import CatalogsManager from './CatalogsManager';
import PortsManager from './PortsManager';
import DiscountManager from './DiscountManager';
import { settingsService } from '../services/settingsService';
import { parseErrorMessage } from '../utils/helpers';
const SettingsManager = ({ 
  goals, 
  onUpdateGoals,
  onUpdateQuarterlyGoals,
  quarterlyGoals = {},
  onImportContacts = () => {},
  contacts = []
}) => {
  const [editGoals, setEditGoals] = useState(goals);
  const [editQuarterlyGoals, setEditQuarterlyGoals] = useState(quarterlyGoals);
  const [exchangeRates, setExchangeRates] = useState({
    usd: 0,
    eur: 0
  });
  const [loadingRates, setLoadingRates] = useState(false);
  const [activeSection, setActiveSection] = useState('general'); // 'general', 'users', 'catalogs', 'ports', 'contact', 'discounts'
  const [activeContactTab, setActiveContactTab] = useState('payment-conditions');
  const [companyData, setCompanyData] = useState({
    name: '',
    data: '',
    logo: ''
  });
  const [loadingCompany, setLoadingCompany] = useState(false);
  const [logoPreview, setLogoPreview] = useState(null);
  const [logoutTimeout, setLogoutTimeout] = useState(0); // En minutos, 0 = nunca se desloguea
  const [loadingTimeout, setLoadingTimeout] = useState(false);
  
  // Sincronizar el estado interno con las props si cambian
  useEffect(() => {
    setEditGoals(goals);
  }, [goals]);

  useEffect(() => {
    setEditQuarterlyGoals(quarterlyGoals);
  }, [quarterlyGoals]);

  // Cargar datos de empresa y timeout al montar
  useEffect(() => {
    loadCompanyData();
    loadLogoutTimeout();
  }, []);

  const loadCompanyData = async () => {
    try {
      const data = await settingsService.getCompanyData();
      setCompanyData(data);
      if (data.logo) {
        setLogoPreview(data.logo);
      }
    } catch (error) {
      console.error('Error cargando datos de empresa:', error);
    }
  };

  const loadLogoutTimeout = async () => {
    try {
      const timeout = await settingsService.getLogoutTimeout();
      setLogoutTimeout(timeout);
    } catch (error) {
      console.error('Error cargando timeout de deslogueo:', error);
    }
  };

  const handleSaveLogoutTimeout = async () => {
    setLoadingTimeout(true);
    try {
      await settingsService.setLogoutTimeout(logoutTimeout);
      alert('Tiempo de inactividad guardado correctamente.');
    } catch (error) {
      console.error('Error guardando timeout de deslogueo:', error);
      alert('No se pudo guardar el tiempo de inactividad. Por favor intentá de nuevo.');
    } finally {
      setLoadingTimeout(false);
    }
  };

  const handleCompanyDataChange = (field, value) => {
    setCompanyData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleLogoUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validar que sea una imagen
    if (!file.type.startsWith('image/')) {
      alert('El archivo seleccionado no es una imagen válida. Seleccioná un archivo JPG, PNG o GIF.');
      return;
    }

    // Validar tamaño (máximo 2MB)
    if (file.size > 2 * 1024 * 1024) {
      alert('La imagen supera el tamaño máximo permitido (2 MB). Seleccioná una imagen más pequeña.');
      return;
    }

    setLoadingCompany(true);
    try {
      const base64Logo = await settingsService.uploadCompanyLogo(file);
      setCompanyData(prev => ({ ...prev, logo: base64Logo }));
      setLogoPreview(base64Logo);
      alert('Logo guardado correctamente.');
    } catch (error) {
      console.error('Error subiendo logo:', error);
      alert('No se pudo guardar el logo. Por favor intentá de nuevo.');
    } finally {
      setLoadingCompany(false);
    }
  };

  const handleSaveCompanyData = async () => {
    setLoadingCompany(true);
    try {
      await settingsService.setCompanyData(companyData);
      alert('Datos de empresa guardados correctamente.');
    } catch (error) {
      console.error('Error guardando datos de empresa:', error);
      alert('No se pudieron guardar los datos de empresa. Por favor intentá de nuevo.');
    } finally {
      setLoadingCompany(false);
    }
  };

  const handleRemoveLogo = () => {
    setCompanyData(prev => ({ ...prev, logo: '' }));
    setLogoPreview(null);
  };

  // Cargar tipos de cambio al montar
  useEffect(() => {
    loadExchangeRates();
  }, []);

  const loadExchangeRates = async () => {
    try {
      const [usdRate, eurRate] = await Promise.all([
        settingsService.getExchangeRate('USD'),
        settingsService.getExchangeRate('EUR')
      ]);
      setExchangeRates({
        usd: usdRate || 0,
        eur: eurRate || 0
      });
    } catch (error) {
      console.error('Error cargando tipos de cambio:', error);
    }
  };

  const handleExchangeRateChange = async (currency, value) => {
    const rate = parseFloat(value) || 0;
    setExchangeRates(prev => ({
      ...prev,
      [currency.toLowerCase()]: rate
    }));
  };

  const handleSaveExchangeRates = async () => {
    // Validar que los valores sean mayores a 0
    if (exchangeRates.usd <= 0 && exchangeRates.eur <= 0) {
      alert('Ingresá al menos un tipo de cambio con valor mayor a 0.');
      return;
    }
    
    if (exchangeRates.usd <= 0) {
      alert('El tipo de cambio USD debe ser mayor a 0.');
      return;
    }
    
    if (exchangeRates.eur <= 0) {
      alert('El tipo de cambio EUR debe ser mayor a 0.');
      return;
    }
    
    setLoadingRates(true);
    try {
      // Solo guardar los valores que son mayores a 0
      const promises = [];
      if (exchangeRates.usd > 0) {
        promises.push(settingsService.setExchangeRate('USD', exchangeRates.usd));
      }
      if (exchangeRates.eur > 0) {
        promises.push(settingsService.setExchangeRate('EUR', exchangeRates.eur));
      }
      
      await Promise.all(promises);
      alert('Tipos de cambio guardados correctamente.');
    } catch (error) {
      console.error('Error guardando tipos de cambio:', error);
      alert(`No se pudieron guardar los tipos de cambio.\n${parseErrorMessage(error)}`);
    } finally {
      setLoadingRates(false);
    }
  };

  const handleGoalUpdate = (key, value) => {
    setEditGoals(prevGoals => ({
      ...prevGoals,
      [key]: {
        ...prevGoals[key],
        target: parseInt(value) || 0
      }
    }));
  };

  const handleSave = async () => {
    onUpdateGoals(editGoals);
    if (onUpdateQuarterlyGoals) {
      onUpdateQuarterlyGoals(editQuarterlyGoals);
    }
    // Guardar objetivos trimestrales en el backend
    try {
      await settingsService.setQuarterlyGoals(editQuarterlyGoals);
    } catch (error) {
      console.error('Error guardando objetivos trimestrales:', error);
      alert('No se pudieron guardar los objetivos en el servidor. Los cambios se guardaron localmente.');
    }
  };

  const handleQuarterlyGoalUpdate = async (quarter, value) => {
    const updatedGoals = {
      ...editQuarterlyGoals,
      [quarter]: parseFloat(value) || 0
    };
    setEditQuarterlyGoals(updatedGoals);
    
    // Guardar automáticamente en el backend cuando se actualiza
    try {
      await settingsService.setQuarterlyGoals(updatedGoals);
      if (onUpdateQuarterlyGoals) {
        onUpdateQuarterlyGoals(updatedGoals);
      }
    } catch (error) {
      console.error('Error guardando objetivo trimestral:', error);
      // No mostrar alerta para no interrumpir la experiencia del usuario
    }
  };

  const handleReset = () => {
    setEditGoals(goals); // Vuelve a los últimos objetivos guardados
    setEditQuarterlyGoals(quarterlyGoals);
  };

  const goalItems = [
    { key: 'meetings', label: 'Reuniones Semanales', icon: Users, unit: 'reuniones' },
    { key: 'contacts', label: 'Intentos de Contacto por Semana (considera 5 días hábiles)', icon: Phone, unit: 'contactos' }, // Cambiado a semanal para que tenga sentido con otras metas
    { key: 'leads', label: 'Nuevos Leads Semanales', icon: UserPlus, unit: 'leads' },
  ];

  return (
    <div
      className="bg-white border border-gray-200 rounded-xl p-8 shadow-sm"
    >
      <div className="flex items-center gap-4 mb-6">
        <div className="p-3 bg-blue-600 rounded-xl">
          <Settings className="w-8 h-8 text-white" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-gray-800">
            Configuración
          </h1>
          <p className="text-gray-500 font-medium">Ajusta tus metas comerciales y parámetros del sistema</p>
        </div>
      </div>

      {/* Tabs de secciones */}
      <div className="flex gap-4 border-b border-gray-200 mb-6">
        <button
          onClick={() => setActiveSection('general')}
          className={`px-6 py-3 font-semibold transition-colors ${
            activeSection === 'general'
              ? 'text-blue-600 border-b-2 border-blue-600'
              : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          <Settings className="w-5 h-5 inline mr-2" />
          General
        </button>
        <button
          onClick={() => setActiveSection('users')}
          className={`px-6 py-3 font-semibold transition-colors ${
            activeSection === 'users'
              ? 'text-blue-600 border-b-2 border-blue-600'
              : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          <Shield className="w-5 h-5 inline mr-2" />
          Usuarios y Permisos
        </button>
        <button
          onClick={() => setActiveSection('catalogs')}
          className={`px-6 py-3 font-semibold transition-colors ${
            activeSection === 'catalogs'
              ? 'text-blue-600 border-b-2 border-blue-600'
              : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          <FileText className="w-5 h-5 inline mr-2" />
          Configuración varios
        </button>
        <button
          onClick={() => setActiveSection('ports')}
          className={`px-6 py-3 font-semibold transition-colors ${
            activeSection === 'ports'
              ? 'text-blue-600 border-b-2 border-blue-600'
              : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          <Anchor className="w-5 h-5 inline mr-2" />
          Puertos
        </button>
        <button
          onClick={() => setActiveSection('contact')}
          className={`px-6 py-3 font-semibold transition-colors ${
            activeSection === 'contact'
              ? 'text-blue-600 border-b-2 border-blue-600'
              : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          <Contact className="w-5 h-5 inline mr-2" />
          Contacto
        </button>
        <button
          onClick={() => setActiveSection('discounts')}
          className={`px-6 py-3 font-semibold transition-colors ${
            activeSection === 'discounts'
              ? 'text-blue-600 border-b-2 border-blue-600'
              : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          <Percent className="w-5 h-5 inline mr-2" />
          Descuentos
        </button>
      </div>

      {activeSection === 'users' ? (
        <UserManagement />
      ) : activeSection === 'catalogs' ? (
        <CatalogsManager />
      ) : activeSection === 'ports' ? (
        <PortsManager />
      ) : activeSection === 'discounts' ? (
        <DiscountManager />
      ) : activeSection === 'contact' ? (
        <div className="space-y-4">
          {/* Sub-tabs de Contacto */}
          <div className="flex gap-2 border-b border-gray-200">
            <button
              onClick={() => setActiveContactTab('payment-conditions')}
              className={`px-5 py-2.5 text-sm font-semibold transition-colors ${
                activeContactTab === 'payment-conditions'
                  ? 'text-blue-600 border-b-2 border-blue-600'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Condición de pago
            </button>
          </div>

          {activeContactTab === 'payment-conditions' && (
            <CatalogsManager
              allowedTabs={['paymentMethods', 'creditDays', 'balancePayments', 'advancePayments']}
              initialTab="paymentMethods"
            />
          )}
        </div>
      ) : (
      <div className="space-y-6">
        {/* Sección de Configuración de Empresa */}
        <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">

          <div className="flex items-center gap-3 mb-4">
            <Building2 className="w-6 h-6 text-blue-600" />
            <h2 className="text-xl font-bold text-gray-800">Datos de Empresa para Cotizaciones</h2>
          </div>
          <p className="text-sm text-gray-600 mb-4">
            Configura el nombre, datos y logo de tu empresa que aparecerán en las cotizaciones generadas. El logo se mostrará en el margen superior izquierdo de todas las páginas.
          </p>
          
          <div className="space-y-4">
            {/* Nombre de la empresa */}
            <div className="bg-white rounded-lg p-4 border border-gray-200">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Nombre de la Empresa
              </label>
              <input
                type="text"
                value={companyData.name}
                onChange={(e) => handleCompanyDataChange('name', e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/30"
                placeholder="Ej: VINSON TECH SRL"
              />
            </div>

            {/* Datos de la empresa */}
            <div className="bg-white rounded-lg p-4 border border-gray-200">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Datos de la Empresa
              </label>
              <textarea
                value={companyData.data}
                onChange={(e) => handleCompanyDataChange('data', e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/30 resize-none"
                rows={4}
                placeholder="Ej: Vinson SRL - Soluciones Industriales&#10;www.vinson.com.ar | soporte@vinson.com.ar"
              />
            </div>

            {/* Logo de la empresa */}
            <div className="bg-white rounded-lg p-4 border border-gray-200">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Logo de la Empresa
              </label>
              <p className="text-xs text-gray-500 mb-3">
                El logo aparecerá en el margen superior izquierdo de todas las páginas de las cotizaciones. Formatos soportados: PNG, JPG, SVG (máx. 2MB).
              </p>
              
              <div className="flex items-start gap-4">
                {/* Vista previa del logo */}
                {logoPreview && (
                  <div className="flex-shrink-0">
                    <div className="relative">
                      <img
                        src={logoPreview}
                        alt="Logo de la empresa"
                        className="w-24 h-24 object-contain border border-gray-200 rounded-lg bg-white p-2"
                        style={{ maxWidth: '96px', maxHeight: '96px' }}
                      />
                      <button
                        onClick={handleRemoveLogo}
                        className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs hover:bg-red-600 transition-colors"
                        title="Eliminar logo"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  </div>
                )}

                {/* Botón de carga */}
                <div className="flex-1">
                  <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors">
                    <div className="flex flex-col items-center justify-center pt-5 pb-6">
                      {logoPreview ? (
                        <>
                          <ImageIcon className="w-8 h-8 text-gray-400 mb-2" />
                          <p className="text-sm text-gray-600">Cambiar logo</p>
                        </>
                      ) : (
                        <>
                          <Upload className="w-8 h-8 text-gray-400 mb-2" />
                          <p className="text-sm text-gray-600">Click para subir logo</p>
                          <p className="text-xs text-gray-500 mt-1">PNG, JPG, SVG (máx. 2MB)</p>
                        </>
                      )}
                    </div>
                    <input
                      type="file"
                      className="hidden"
                      accept="image/*"
                      onChange={handleLogoUpload}
                      disabled={loadingCompany}
                    />
                  </label>
                </div>
              </div>
            </div>

            {/* Botón guardar */}
            <div className="flex justify-end">
              <button
                onClick={handleSaveCompanyData}
                disabled={loadingCompany}
                className="px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-semibold shadow-sm transition-all duration-300 flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Save className="w-5 h-5" />
                {loadingCompany ? 'Guardando...' : 'Guardar Datos de Empresa'}
              </button>
            </div>
          </div>
        </div>

        {/* Separador */}
        <div className="border-t border-gray-200 my-6"></div>

        {/* Sección de Tipos de Cambio */}
        <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">

          <div className="flex items-center gap-3 mb-4">
            <DollarSign className="w-6 h-6 text-green-600" />
            <h2 className="text-xl font-bold text-gray-800">Tipos de Cambio</h2>
          </div>
          <p className="text-sm text-gray-600 mb-4">
            Configura los tipos de cambio para convertir abonos cotizados en otras monedas a pesos argentinos (ARS). Estos valores se utilizan para calcular el indicador de "Abonos Cotizados".
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div className="bg-white rounded-lg p-4 border border-gray-200">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Tipo de Cambio USD → ARS
              </label>
              <input
                type="number"
                value={exchangeRates.usd || ''}
                onChange={(e) => handleExchangeRateChange('USD', e.target.value)}
                className="w-full px-4 py-2 text-sm bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500 text-lg font-semibold"
                min="0.01"
                step="0.01"
                placeholder="0.00"
              />
            </div>
            <div className="bg-white rounded-lg p-4 border border-gray-200">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Tipo de Cambio EUR → ARS
              </label>
                      <input
                        type="number"
                        value={exchangeRates.eur || ''}
                        onChange={(e) => handleExchangeRateChange('EUR', e.target.value)}
                        className="w-full px-4 py-2 text-sm bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500 text-lg font-semibold"
                        min="0.01"
                        step="0.01"
                        placeholder="0.00"
                      />
            </div>
          </div>
          <div className="flex justify-end">
            <button
              onClick={handleSaveExchangeRates}
              disabled={loadingRates}
              className="px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-semibold shadow-sm transition-all duration-300 flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Save className="w-5 h-5" />
              {loadingRates ? 'Guardando...' : 'Guardar Tipos de Cambio'}
            </button>
          </div>
        </div>

        {/* Separador */}
        <div className="border-t border-gray-200 my-6"></div>

        {/* Sección de Timeout de Deslogueo */}
        <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">

          <div className="flex items-center gap-3 mb-4">
            <Shield className="w-6 h-6 text-blue-600" />
            <h2 className="text-xl font-bold text-gray-800">Timeout de Deslogueo Automático</h2>
          </div>
          <p className="text-sm text-gray-600 mb-4">
            Configura el tiempo de inactividad (en minutos) después del cual el usuario será deslogueado automáticamente. 
            Si estableces 0, el usuario nunca será deslogueado automáticamente.
          </p>
          <div className="bg-white rounded-lg p-4 border border-gray-200">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Tiempo de Inactividad (minutos)
            </label>
            <input
              type="number"
              value={logoutTimeout}
              onChange={(e) => setLogoutTimeout(parseInt(e.target.value) || 0)}
              className="w-full px-4 py-2 text-sm bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500 text-lg font-semibold"
              min="0"
              step="1"
              placeholder="0"
            />
            <p className="text-xs text-gray-500 mt-2">
              {logoutTimeout === 0 
                ? 'El usuario nunca será deslogueado automáticamente.' 
                : `El usuario será deslogueado después de ${logoutTimeout} minuto(s) de inactividad.`}
            </p>
          </div>
          <div className="flex justify-end mt-4">
            <button
              onClick={handleSaveLogoutTimeout}
              disabled={loadingTimeout}
              className="px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-semibold shadow-sm transition-all duration-300 flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Save className="w-5 h-5" />
              {loadingTimeout ? 'Guardando...' : 'Guardar Timeout'}
            </button>
          </div>
        </div>

        {/* Separador */}
        <div className="border-t border-gray-200 my-6"></div>

        {/* Sección de Importación Masiva */}
        <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">

          <div className="flex items-center gap-3 mb-4">
            <Settings className="w-6 h-6 text-blue-600" />
            <h2 className="text-xl font-bold text-gray-800">Importación Masiva de Datos</h2>
          </div>
          <p className="text-sm text-gray-600 mb-4">
            Importa empresas y contactos desde un archivo Excel. El sistema procesará las relaciones entre ellos automáticamente.
          </p>
          <BulkImporter
            onImportContacts={onImportContacts}
            existingContacts={contacts}
          />
        </div>

        {/* Separador */}
        <div className="border-t border-gray-200 my-6"></div>
        
        {/* Sección de Objetivos Trimestrales */}
        <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm mb-6">

          <div className="flex items-center gap-3 mb-4">
            <DollarSign className="w-6 h-6 text-blue-600" />
            <h2 className="text-xl font-bold text-gray-800">Objetivos Trimestrales de Facturación</h2>
          </div>
          <p className="text-sm text-gray-600 mb-4">
            Establece los objetivos de facturación para cada trimestre del año. Estos objetivos se utilizan para calcular el progreso en el Dashboard.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {[1, 2, 3, 4].map(quarter => {
              const quarterKey = `q${quarter}`;
              const currentValue = editQuarterlyGoals[quarterKey] || editQuarterlyGoals[`Q${quarter}`] || 0;
              
              return (
                <div
                  key={quarterKey}
                  className="flex flex-col sm:flex-row items-start sm:items-center gap-4 bg-white border border-gray-200 rounded-xl p-4"
                >
                  <div className="flex items-center gap-3 flex-1">
                    <DollarSign className="w-5 h-5 text-blue-500" />
                    <h3 className="font-semibold text-gray-800">Objetivo Q{quarter}:</h3>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-gray-500">USD</span>
                    <EditableField
                      value={currentValue.toString()}
                      onSave={(value) => handleQuarterlyGoalUpdate(quarterKey, value)}
                      type="number"
                      className="px-3 py-1 bg-white rounded-lg text-lg font-bold min-w-[120px] text-right"
                      required
                      validation={(val) => (parseFloat(val) < 0 ? 'No negativo' : null)}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Separador */}
        <div className="border-t border-gray-200 my-6"></div>
        
        {/* Título de Objetivos */}
        <h2 className="text-xl font-bold text-gray-800 mb-4">Objetivos Comerciales Semanales</h2>
        {goalItems.map((item, index) => {
          const Icon = item.icon;
          const currentValue = editGoals[item.key]?.target || 0;
          
          return (
            <div
              key={item.key}
              className="flex flex-col sm:flex-row items-start sm:items-center gap-4 bg-gray-50 border border-gray-200 rounded-xl p-6"
            >
              <div className="flex items-center gap-4 flex-1">
                <Icon className="w-6 h-6 text-blue-500" />
                <h3 className="font-semibold text-gray-800">{item.label}:</h3>
              </div>
              
              <div className="flex items-center gap-2">
                <EditableField
                  value={currentValue.toString()}
                  onSave={(value) => handleGoalUpdate(item.key, value)}
                  type="number"
                  className="px-3 py-1 bg-white rounded-lg text-lg font-bold min-w-[80px] text-right"
                  required
                  validation={(val) => (parseInt(val) < 0 ? 'No negativo' : null)}
                />
                <span className="text-lg text-gray-600">{item.unit}</span>
              </div>
            </div>
          );
        })}
        
        <div className="flex justify-end gap-4 mt-8">
          <button
            onClick={handleReset}
            className="px-6 py-3 bg-gray-200 text-gray-700 rounded-lg font-semibold hover:bg-gray-300 transition-all duration-300 flex items-center gap-2"
          >
            <X className="w-5 h-5" />
            Cancelar
          </button>
          <button
            onClick={handleSave}
            className="px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-semibold shadow-sm transition-all duration-300 flex items-center gap-2"
          >
            <Save className="w-5 h-5" />
            Guardar Objetivos
          </button>
        </div>
      </div>
      )}
    </div>
  );
};

export default SettingsManager;
